/**
 * O guia de uma conexão, desenhado para ser lido COM o formulário aberto.
 *
 * Um componente, três telas (Meta Ads, Monitoradores, Redirecionadores). O conteúdo mora
 * em `conexoes.ts` e não se repete aqui — texto duplicado diverge na primeira correção e
 * ninguém descobre qual das duas cópias está na tela.
 *
 * ### Duas decisões de UX que mudam o que a pessoa consegue fazer
 *
 * **1. "Se não funcionar" nasce ABERTA, sempre.** É a seção que muda o resultado (ver o
 * cabeçalho de `conexoes.ts`): ela existe para quem está no meio do erro, não para quem lê
 * por curiosidade. Quem chega aqui já fez tudo "certo" e nada aconteceu — pedir um clique a
 * mais nesse momento é esconder a resposta atrás da pergunta que a pessoa ainda não sabe
 * formular. Além disso, o Ctrl+F do navegador não acha com segurança o que está dentro de
 * um bloco fechado, e procurar o próprio sintoma na página é exatamente o que ela vai
 * tentar fazer.
 *
 * **2. Com a conexão JÁ feita, o guia vira uma linha que abre (`recolhido`).** Ele não pode
 * ocupar a tela de quem já conectou — essa pessoa vem ver número, não tutorial —, mas
 * também não pode sumir: quando quebra, é para esta mesma tela que ela volta, e o guia
 * precisa estar onde ela já está olhando, não num curso noutra aba. Recolhido é `<details>`
 * nativo: abre por clique ou por teclado, e o conteúdo continua no documento.
 *
 * ### Detalhes que parecem enfeite e não são
 *
 * - O **número do passo é conteúdo**: é a ORDEM. Fica visível e em `nums` (tabular), porque
 *   "o passo 3" é como as pessoas conversam sobre isto no WhatsApp.
 * - O `atencao` é destacado por **barra + palavra + ícone**, nunca só por cor
 *   (DESIGN_SYSTEM §1.3), e com fundo SÓLIDO: fundo translúcido por cima de texto deixa os
 *   dois ilegíveis no celular, e isso já aconteceu neste painel.
 * - O `exemplo` vem com botão de copiar de verdade (`CopyBlock`), porque o erro que abriu
 *   esta tarefa foi digitar um nome de variável à mão e errar uma letra.
 */
import { AlertTriangle, ChevronDown, LifeBuoy } from 'lucide-react';
import { Card, cn } from '@mgdlead/ui';

import { CopyBlock } from '../../pages/workspace/redirectors/CopyBlock';
import type { GuiaDeConexao as Guia, PassoDeGuia, SintomaEcausa } from './conexoes';

export interface GuiaDeConexaoProps {
  guia: Guia;
  /**
   * `true` quando a conexão já existe: o guia encolhe para uma linha que abre.
   * `false` (padrão) quando não existe nenhuma — aí ele É a tela.
   */
  recolhido?: boolean;
  className?: string;
}

export function GuiaDeConexao({ guia, recolhido = false, className }: GuiaDeConexaoProps) {
  const idTitulo = `guia-${guia.id}`;

  if (!recolhido) {
    return (
      <Card padded={false} className={className}>
        <section aria-labelledby={idTitulo} className="flex flex-col gap-3 p-3 md:p-4">
          <div className="flex flex-col gap-1">
            <h2 id={idTitulo} className="text-md font-medium">
              {guia.titulo}
            </h2>
            <p className="max-w-prose text-sm text-muted-foreground">{guia.resumo}</p>
          </div>
          <Passos passos={guia.passos} />
          <SeNaoFuncionar itens={guia.quandoNaoFunciona} />
        </section>
      </Card>
    );
  }

  return (
    <Card padded={false} className={className}>
      <details className="group">
        {/*
          `<summary>` nativo: já é foco de teclado, já abre no Enter e já anuncia o estado.
          Recriar isso com botão + estado daria o mesmo desenho com menos acessibilidade.
        */}
        <summary
          className={cn(
            'flex cursor-pointer list-none items-center justify-between gap-3 p-3 md:p-4',
            'rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'hover:bg-muted/40 [&::-webkit-details-marker]:hidden',
          )}
        >
          <span className="min-w-0 flex flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground">{guia.titulo}</span>
            <span className="text-2xs text-muted-foreground">
              O passo a passo e o que olhar quando parar de funcionar
            </span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className="size-4 shrink-0 text-muted-foreground transition-transform duration-[var(--duration-micro)] group-open:rotate-180"
          />
        </summary>

        <div className="flex flex-col gap-3 border-t border-border p-3 md:p-4">
          <p className="max-w-prose text-sm text-muted-foreground">{guia.resumo}</p>
          <Passos passos={guia.passos} />
          <SeNaoFuncionar itens={guia.quandoNaoFunciona} />
        </div>
      </details>
    </Card>
  );
}

/* ========================================================================== */
/*  Os passos, na ordem                                                        */
/* ========================================================================== */

function Passos({ passos }: { passos: readonly PassoDeGuia[] }) {
  return (
    <ol className="flex flex-col gap-4">
      {passos.map((passo, indice) => (
        <li key={passo.titulo} className="flex gap-3">
          {/*
            O número é conteúdo (é a ORDEM). `aria-hidden` porque o `<ol>` já numera para o
            leitor de tela — o que está aqui é a mesma informação para quem enxerga.
          */}
          <span
            aria-hidden="true"
            className="nums flex size-6 shrink-0 items-center justify-center rounded-sm border border-border text-xs font-medium text-muted-foreground"
          >
            {indice + 1}
          </span>

          <div className="min-w-0 flex flex-col gap-1.5">
            <p className="text-sm font-medium text-foreground">{passo.titulo}</p>
            <p className="max-w-prose text-sm text-muted-foreground">{passo.texto}</p>

            {passo.exemplo !== undefined ? (
              <div className="mt-0.5 max-w-prose">
                <CopyBlock value={passo.exemplo} what="Exemplo" />
              </div>
            ) : null}

            {passo.atencao !== undefined ? <Atencao>{passo.atencao}</Atencao> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * O detalhe que faz o passo "parecer feito" sem estar.
 *
 * Barra à esquerda + ícone + a palavra "Atenção": três canais, nenhum deles só a cor. O
 * fundo é `bg-muted` SÓLIDO de propósito — translúcido sobre texto foi o que deixou um
 * aviso ilegível no celular; e o corpo fica em `text-foreground`, porque parágrafo inteiro
 * em cor de alerta cansa e some no fundo escuro.
 */
function Atencao({ children }: { children: string }) {
  return (
    <div className="mt-1 max-w-prose rounded-sm border-l-2 border-warning bg-muted px-3 py-2">
      <p className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-warning">
        <AlertTriangle aria-hidden="true" className="size-3.5 shrink-0" />
        Atenção
      </p>
      <p className="mt-1 text-sm text-foreground">{children}</p>
    </div>
  );
}

/* ========================================================================== */
/*  Se não funcionar                                                           */
/* ========================================================================== */

/**
 * Sintoma → causa. Sempre visível, nunca atrás de um clique (ver o cabeçalho).
 *
 * `<dl>` de verdade: o sintoma é o termo que a pessoa procura e a causa é a definição
 * dele. Isso dá ao leitor de tela o mesmo pareamento que o olho vê.
 */
function SeNaoFuncionar({ itens }: { itens: readonly SintomaEcausa[] }) {
  return (
    <section className="rounded-md border border-border-strong p-3">
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <LifeBuoy aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        Se não funcionar
      </h3>

      <dl className="mt-2 flex flex-col gap-3">
        {itens.map((item) => (
          <div key={item.sintoma} className="flex flex-col gap-0.5">
            <dt className="max-w-prose text-sm font-medium text-foreground">{item.sintoma}</dt>
            <dd className="max-w-prose text-sm text-muted-foreground">{item.causa}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
